import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { getCustomer, searchVehicles } from "../api";

function formatDateTime(value) {
  if (!value) {
    return "—";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });
}

function formatCustomerDisplay(customer) {
  if (!customer) {
    return "—";
  }
  if (customer.company) {
    return customer.company;
  }
  const parts = [customer.firstName, customer.lastName].filter(Boolean);
  if (parts.length === 0) {
    return "—";
  }
  return parts.join(" ");
}

function isVehicleUnverified(vehicle) {
  if (!vehicle) {
    return true;
  }
  const hasVin = Boolean(vehicle.vin);
  const hasHsnTsn = Boolean(vehicle.hsn) && Boolean(vehicle.tsn);
  return !(hasVin || hasHsnTsn);
}

function formatModel(vehicle) {
  if (!vehicle) {
    return "—";
  }
  const parts = [vehicle.make, vehicle.model, vehicle.modelVariant].filter(Boolean);
  if (parts.length === 0) {
    return "—";
  }
  return parts.join(" ");
}

export default function VehiclesPage() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["vehicles", { query: debouncedSearch }],
    queryFn: async () => {
      const vehicles = await searchVehicles(token, { query: debouncedSearch || undefined });
      const uniqueCustomerIds = Array.from(
        new Set(vehicles.map((item) => item.customerId).filter(Boolean))
      );
      if (uniqueCustomerIds.length === 0) {
        return { vehicles, customerMap: {} };
      }
      const results = await Promise.allSettled(
        uniqueCustomerIds.map((customerId) => getCustomer(token, customerId))
      );
      const customerMap = {};
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value) {
          customerMap[result.value.id] = result.value;
        }
      });
      return { vehicles, customerMap };
    },
    enabled: Boolean(token),
    keepPreviousData: true,
  });

  const vehicles = data?.vehicles ?? [];
  const customerMap = data?.customerMap ?? {};

  const showTable = !isLoading && !error && vehicles.length > 0;
  const showEmpty = !isLoading && !error && vehicles.length === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          Fahrzeuge
        </Typography>
        <TextField
          label="Suche"
          placeholder="Kennzeichen, VIN, Marke oder Modell"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Paper sx={{ position: "relative" }}>
          {isFetching && !isLoading && (
            <LinearProgress
              sx={{ position: "absolute", top: 0, left: 0, right: 0, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
            />
          )}
          {isLoading ? (
            <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ py: 6, px: 3 }}>
              <Typography color="error" variant="body1">
                {error.message || "Fehler beim Laden der Fahrzeuge."}
              </Typography>
            </Box>
          ) : (
            <>
              {showTable && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Kennzeichen</TableCell>
                      <TableCell>Kunde</TableCell>
                      <TableCell>Modell</TableCell>
                      <TableCell>Einwilligung</TableCell>
                      <TableCell>Letzte Aktivität</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vehicles.map((vehicle) => {
                      const customer = vehicle.customerId ? customerMap[vehicle.customerId] : null;
                      return (
                        <TableRow
                          key={vehicle.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">
                                {vehicle.licensePlate || "—"}
                              </Typography>
                              {isVehicleUnverified(vehicle) && (
                                <Chip label="UNVERIFIED" color="warning" size="small" variant="outlined" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{formatCustomerDisplay(customer)}</TableCell>
                          <TableCell>{formatModel(vehicle)}</TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                vehicle.consentGiven
                                  ? `Erteilt: ${formatDateTime(vehicle.consentTimestamp)}`
                                  : "Keine Einwilligung dokumentiert"
                              }
                            >
                              <Chip
                                label={vehicle.consentGiven ? "ja" : "nein"}
                                color={vehicle.consentGiven ? "success" : "default"}
                                size="small"
                                variant={vehicle.consentGiven ? "filled" : "outlined"}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>{formatDateTime(vehicle.updatedAt)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {showEmpty && (
                <Box sx={{ py: 6, px: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine Fahrzeuge gefunden.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  );
}
